import { RefreshCw } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import { ModuleCard } from '@/components/os/ModuleCard';
import { ModuleGrid, ModuleGridItem } from '@/components/os/ModuleGrid';
import { moduleDashboardPath, visibleModules } from '@/app/router/modules';
import { useAuth } from '@/features/auth/useAuth';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { LiveClock } from '@/components/os/LiveClock';
import { useModuleBadges } from './useModuleBadges';


/**
 * School OS home: "which area of the school do I want to work in?"
 *
 * The launcher is navigation first. The only figures on it are the small
 * badges, each a real number from an existing endpoint the user may call; the
 * full picture lives on the School overview page.
 */
export function HomePage() {
  const { can } = useAuth();

  // Students and parents have one place to be: their portal.
  if (can(PERMISSIONS.STUDENT_SELF_VIEW)) {
    return <Navigate to={ROUTES.myOverview} replace />;
  }
  if (can(PERMISSIONS.STUDENT_CHILD_VIEW) && !can(PERMISSIONS.STUDENT_VIEW)) {
    return <Navigate to={ROUTES.myChildren} replace />;
  }
  return <Launcher />;
}

function Launcher() {
  const { user, canAny } = useAuth();
  const { campusId, campuses } = useWorkingContext();
  const modules = visibleModules(canAny);
  const badges = useModuleBadges();

  const campusName = campusId === null ? null : campuses.find((campus) => campus.id === campusId)?.name ?? null;

  return (
    <div className="mx-auto w-full max-w-[1400px]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {campusName !== null && <p className="text-[13px] text-muted">{campusName}</p>}
          <h1 className="mt-1 text-[28px] leading-tight tracking-[-0.02em] text-ink sm:text-[32px]">
            Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
          </h1>
          <p className="mt-1.5 text-[14.5px] text-muted">Here&apos;s what&apos;s happening at your school today.</p>
        </div>
        <LiveClock timeZone={user?.schoolTimezone} className="w-full sm:w-auto" />
      </header>

      {badges.failed && (
        <div
          role="status"
          className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-card px-4 py-3 text-[13px] text-muted"
        >
          <span>Some figures couldn&apos;t be loaded just now. The modules below still work.</span>
          <button
            type="button"
            onClick={badges.retry}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium text-ink hover:bg-surface focus-visible:outline-2 focus-visible:outline-[var(--sms-accent-ring)]"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Try again
          </button>
        </div>
      )}

      <section className="mt-8" aria-labelledby="modules-heading">
        <h2 id="modules-heading" className="mb-4 text-[13px] font-semibold uppercase tracking-[0.08em] text-muted">
          Modules
        </h2>

        {modules.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-card px-6 py-12 text-center">
            <p className="text-[15px] font-semibold text-ink">No modules are available to your account yet</p>
            <p className="mx-auto mt-1.5 max-w-[48ch] text-[13.5px] text-muted">
              Ask a school administrator to give your account a role.
            </p>
          </div>
        ) : (
          <ModuleGrid label="Modules">
            {modules.map((module) => (
              <ModuleGridItem key={module.key}>
                <ModuleCard
                  id={`module-${module.key}`}
                  size="lg"
                  title={module.title}
                  description={module.description}
                  icon={module.icon}
                  accent={module.accent}
                  to={moduleDashboardPath(module.key)}
                  badge={
                    badges.modules[module.key] ??
                    (badges.loading ? <span className="skeleton inline-block h-3 w-24 rounded" aria-hidden="true" /> : undefined)
                  }
                />
              </ModuleGridItem>
            ))}
          </ModuleGrid>
        )}
      </section>
    </div>
  );
}
